import React, { Component } from 'react';
import { connect } from 'react-redux';
import Dropzone from 'react-dropzone';
import { Alert, Button } from 'react-bootstrap';
import api from '../helpers/ApiClient';

class EInvoiceConsolidatedUpload extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dropzoneActive: false,
      fileName: '',
      csvText: '',
      rows: [],
      error: null,
      success: null,
      submitting: false,
      selection: 'self-billed' // 'self-billed' | 'sellers' | 'customers'
    };
  }

  onDragEnter = () => this.setState({ dropzoneActive: true });
  onDragLeave = () => this.setState({ dropzoneActive: false });

  onDrop = (files) => {
    if (!files || !files.length) {
      this.setState({ dropzoneActive: false });
      return;
    }
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result || '';
      try {
        const rows = this.parseCSV(text);
        this.setState({ csvText: text, rows, fileName: file.name, error: null, success: null, dropzoneActive: false });
      } catch (err) {
        this.setState({ error: 'Failed to parse CSV. Please ensure it has headers: id,name,price,quantity.', rows: [], csvText: '', dropzoneActive: false });
      }
    };
    reader.onerror = () => this.setState({ error: 'Failed to read file.', dropzoneActive: false });
    reader.readAsText(file);
  };

  // Simple CSV parser that supports quoted values and commas within quotes
  parseCSV = (text) => {
    const lines = text.replace(/\r/g, '').trim().split(/\n/);
    if (!lines.length) return [];
    const header = this.splitCSVLine(lines[0]).map(h => h.trim());

    const wanted = ['id','name','price','quantity'];
    const lowerHeader = header.map(h => h.toLowerCase());
    const indices = wanted.map(w => lowerHeader.indexOf(w));
    if (indices.some(i => i === -1)) {
      throw new Error('Missing required headers');
    }

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line || !line.trim()) continue;
      const cols = this.splitCSVLine(line);
      const obj = {
        id: Number(cols[indices[0]].trim()),
        name: (cols[indices[1]] || '').trim(),
        price: Number((cols[indices[2]] || '0').replace(/,/g, '')),
        quantity: Number((cols[indices[3]] || '1').trim() || '1')
      };
      if (!obj.name) continue;
      data.push(obj);
    }
    return data;
  };

  splitCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        // Handle escaped quotes " or double quotes within quotes "" -> "
        const next = line[i + 1];
        if (inQuotes && next === '"') {
          current += '"';
          i++; // skip next
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current);
    return result;
  };

  onSelectionChange = (e) => this.setState({ selection: e.target.value, success: null, error: null });

  onSubmit = (e) => {
    e.preventDefault();
    const { rows, selection } = this.state;
    if (!rows.length) {
      this.setState({ error: 'Please upload a CSV file first.' });
      return;
    }
    let endpoint = '/api/einvoice/create/consolidated/self-billed';
    if (selection === 'sellers') endpoint = '/api/einvoice/create/consolidated/sellers';
    if (selection === 'customers') endpoint = '/api/einvoice/create/consolidated/customers';

    this.setState({ submitting: true, error: null, success: null });

    api(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orders: rows })
    }).then((res) => {
      this.setState({ submitting: false, success: 'Document generated successfully.', error: null }, () => {
        if (typeof window !== 'undefined' && window.scrollTo) {
          window.scrollTo({ top: 0 });
        }
      });
    }).catch((err) => {
      const msg = (err && (err.message || err.error || err.details)) || 'Failed to generate document.';
      this.setState({ submitting: false, error: msg, success: null });
    });
  };

  renderPreviewTable() {
    const { rows } = this.state;
    if (!rows.length) return null;
    return (
      <div style={{ marginTop: 15 }}>
        <h4>Preview</h4>
        <div className="table-responsive">
          <table className="table table-striped table-bordered table-condensed">
            <thead>
              <tr>
                <th style={{width: 80}}>ID</th>
                <th>Name</th>
                <th style={{width: 120}}>Price</th>
                <th style={{width: 100}}>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx}>
                  <td>{r.id}</td>
                  <td>{r.name}</td>
                  <td>RM {Number(r.price).toFixed(2)}</td>
                  <td>{r.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  render() {
    const { dropzoneActive, submitting, error, success, fileName, selection } = this.state;

    return (
      <div className="container">
        <h2 style={{ margin: '15px 0' }}>Generate Consolidated Document</h2>

        {error && <Alert bsStyle="danger">{error}</Alert>}
        {success && <Alert bsStyle="success">{success}</Alert>}

        <div className="file-upload-wrapper">
          <Dropzone
            multiple={false}
            accept="text/csv,.csv"
            onDrop={this.onDrop}
            onDragEnter={this.onDragEnter}
            onDragLeave={this.onDragLeave}
          >
            {dropzoneActive && (
              <div className="fileupload-dropzone-overlay">Drop your CSV here...</div>
            )}
            <div style={{ padding: 20, border: '2px dashed #bbb', borderRadius: 6, textAlign: 'center' }}>
              <p style={{ margin: 0 }}>
                {fileName ? (
                  <span><strong>{fileName}</strong> selected. Drop another file to replace.</span>
                ) : (
                  <span>Drop your CSV file here, or click to select.</span>
                )}
              </p>
              <p style={{ margin: 0, color: '#888', fontSize: 12 }}>Expected headers: id, name, price, quantity</p>
            </div>
          </Dropzone>
        </div>

        <div style={{ marginTop: 20 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>Document Type</label>
          <label className="radio-inline">
            <input type="radio" name="docType" value="self-billed" checked={selection === 'self-billed'} onChange={this.onSelectionChange} />{' '}
            Self Billed (seller to giftr)
          </label>
          <label className="radio-inline">
            <input type="radio" name="docType" value="sellers" checked={selection === 'sellers'} onChange={this.onSelectionChange} />{' '}
            Consolidated (giftr to sellers)
          </label>
          <label className="radio-inline">
            <input type="radio" name="docType" value="customers" checked={selection === 'customers'} onChange={this.onSelectionChange} />{' '}
            Consolidated (giftr to customers)
          </label>
        </div>

        {this.renderPreviewTable()}

        <div style={{ marginTop: 15 }}>
          <Button bsStyle="primary" disabled={submitting} onClick={this.onSubmit}>
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </div>
    );
  }
}

export default connect((state) => ({}))(EInvoiceConsolidatedUpload);
